import * as z from "zod";
import type { ApiContext } from "../context";
import { type ApiNotAuthorized, InvalidFormField, UnexpectedApiError, ValidationError } from "../errors";
import { parse } from "../model/common";
import { UserProfile } from "../model/user";
import { forward, type Result } from "../result";
import type { Transport } from "../transport";

const MeResponse = z.object({ user: UserProfile });
const PushTokenResponse = z.object({ devices: z.number() });

export class UserApi {
  constructor(private readonly t: Transport) {}

  /** The caller's profile. The backend creates the row from the token on first sight. */
  async me(ctx: ApiContext): Promise<Result<UserProfile, ApiNotAuthorized>> {
    const r = await this.t.get(ctx, "/user/me");
    return forward(
      r,
      (j) => parse(MeResponse, j).user,
      (e) => {
        if (e instanceof ValidationError) throw new UnexpectedApiError(e);
        return e;
      },
    );
  }

  /** Registers this device's FCM token for push. Idempotent; a token held by another account moves here. Returns the device count. */
  async registerPushToken(
    ctx: ApiContext,
    pushToken: string,
  ): Promise<Result<number, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.post(ctx, "/user/push-token", { body: { pushToken } });
    return forward(
      r,
      (j) => parse(PushTokenResponse, j).devices,
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }

  /** Forgets one device, or every device of the caller when no token is given (sign-out). Returns the device count. */
  async removePushToken(
    ctx: ApiContext,
    pushToken?: string,
  ): Promise<Result<number, ApiNotAuthorized | InvalidFormField>> {
    const r = await this.t.delete(ctx, "/user/push-token", { body: pushToken === undefined ? {} : { pushToken } });
    return forward(
      r,
      (j) => parse(PushTokenResponse, j).devices,
      (e) => {
        if (e instanceof ValidationError) return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        return e;
      },
    );
  }
}
